import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COPY = {
  fr: {
    pageTitle: 'Témoignages',
    subtitle: 'Partagez votre expérience avec Glow & You.',
    nameLabel: 'Nom *',
    namePlaceholder: 'Votre nom',
    emailLabel: 'Email *',
    emailPlaceholder: 'vous@exemple.com',
    commentLabel: 'Votre commentaire *',
    commentPlaceholder: 'Partagez votre expérience avec Glow & You...',
    submit: 'Envoyer mon commentaire',
    sending: 'Envoi en cours…',
    error: "Erreur lors de l'envoi du commentaire",
    success: 'Merci ! Votre commentaire a bien été publié sur le site. 💛'
  },
  en: {
    pageTitle: 'Testimonials',
    subtitle: 'Share your experience with Glow & You.',
    nameLabel: 'Name *',
    namePlaceholder: 'Your name',
    emailLabel: 'Email *',
    emailPlaceholder: 'you@example.com',
    commentLabel: 'Your comment *',
    commentPlaceholder: 'Share your experience with Glow & You...',
    submit: 'Send my comment',
    sending: 'Sending…',
    error: 'There was an error sending your comment',
    success: 'Thank you! Your comment has been published on the site. 💛'
  }
};

const TestimonialsPage = ({ language = 'fr' }) => {
  const t = COPY[language] || COPY.fr;
  const [formData, setFormData] = useState({ name: '', email: '', comment: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [approved, setApproved] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${BACKEND_URL}/api/testimonials/approved`)
      .then((res) => setApproved(res.data || []))
      .catch(() => setApproved([]));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/testimonials`, formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', comment: '' });
    } catch (error) {
      console.error('Error submitting comment:', error);
      toast.error(t.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F7] pt-32 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1
            className="text-4xl sm:text-5xl font-light text-[#2C2C2C] mb-6 italic"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {t.pageTitle}
          </h1>
          <div className="w-24 h-1 bg-[#C9A84C] mx-auto mb-6"></div>
          <p className="text-lg text-[#8B7D6B]">{t.subtitle}</p>
        </div>

        {approved.length > 0 && (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-16"
            data-testid="approved-testimonials-page"
          >
            {approved.map((item) => (
              <article
                key={item.id}
                className="bg-white rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow duration-300"
              >
                <p
                  className="text-[#2C2C2C] leading-relaxed mb-4 italic"
                  style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.125rem' }}
                >
                  "{item.comment}"
                </p>
                <div className="border-t border-[#EDE6D6] pt-3">
                  <p className="text-sm font-semibold tracking-wider uppercase text-[#6B2737]">
                    — {item.name}
                  </p>
                </div>
              </article>
            ))}
          </div>
        )}

        <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-8 md:p-12">
          {submitted ? (
            <div data-testid="testimonial-page-success-message" className="text-center py-8">
              <p
                className="text-2xl italic"
                style={{ fontFamily: 'Cormorant Garamond, serif', color: '#6B2737' }}
              >
                {t.success}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="testimonial-page-form">
              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  {t.nameLabel}
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t.namePlaceholder}
                  data-testid="testimonial-page-name-input"
                  className="w-full px-4 py-3 bg-[#F5F3F0] border border-[#D4C4B0] rounded-md text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  {t.emailLabel}
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder={t.emailPlaceholder}
                  data-testid="testimonial-page-email-input"
                  className="w-full px-4 py-3 bg-[#F5F3F0] border border-[#D4C4B0] rounded-md text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  {t.commentLabel}
                </label>
                <textarea
                  required
                  rows={6}
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  placeholder={t.commentPlaceholder}
                  data-testid="testimonial-page-comment-input"
                  className="w-full px-4 py-3 bg-[#F5F3F0] border border-[#D4C4B0] rounded-md text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#C9A84C] resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="testimonial-page-submit-button"
                className="w-full px-8 py-4 text-base font-medium tracking-wider uppercase text-white rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#6B2737' }}
              >
                {isSubmitting ? t.sending : t.submit}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestimonialsPage;
